import { apiGetCustomers } from "@lib/api/customer";
import {
  apiGetProviderCustomerMatrices,
  apiGetProviders,
} from "@lib/api/provider";
import {
  CustomerName,
  ProviderName,
} from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { createYupSync } from "@lib/helpers/TableHelper";
import { useTypedSelector } from "@lib/redux";
import { transactionSchema } from "@lib/schemas/transaction";
import {
  AuthRoles,
  CurrencyEnum,
  TransactionProxyState,
  TransactionProxyStatus,
  TransactionType,
  UseMutationProps,
} from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Select, Space } from "antd";
import { useIntl } from "react-intl";
import { useCallback, useEffect, useState } from "react";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  mutationQuery: UseMutationProps<Partial<TransactionType>>;
};

export default function CreateForm(props: Props) {
  const { mutationQuery } = props;
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const { user } = useTypedSelector((state) => state.auth);
  const role = user?.role;
  const [currency, setCurrency] = useState<CurrencyEnum>(CurrencyEnum.USD);

  const { mutateAsync: createTransaction, isPending: isCreating } =
    mutationQuery;

  const { data: customers } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: role === AuthRoles.Admin,
  });

  const { data: providers } = useQuery({
    queryFn: () => apiGetProviders({ limit: 100 }),
    queryKey: ["providers-dropdown"],
    enabled: role === AuthRoles.Admin,
  });

  const { data: providerCustomerMatrices } = useQuery({
    queryFn: () =>
      apiGetProviderCustomerMatrices({
        limit: 100,
        join: ["provider"],
      }),
    queryKey: ["provider-customer-matrix-dropdown"],
  });

  useEffect(() => {
    if (providerCustomerMatrices?.data?.data?.length == 1) {
      form.setFieldsValue({
        providerId: providerCustomerMatrices?.data?.data[0]?.providerId,
      });
    }
  }, [providerCustomerMatrices]);

  const handleCreate = useCallback(
    (values: any) => {
      const data = {
        providerId: values?.providerId || user?.providerId,
        customerId:
          role === AuthRoles.Admin ? values?.customerId : user?.customerId,
        status:
          values?.providerId && values?.customerId
            ? TransactionProxyStatus.Pending
            : TransactionProxyStatus.Initialized,
        state:
          values?.providerId && !values?.providerCustomerMatrixId
            ? TransactionProxyState.PendingForAccountInfo
            : null,
        currency: currency,
        ...values,
        amount: parseInt(values.amount.toString()),
      };
      createTransaction(data);
    },
    [createTransaction, role, user],
  );

  const handleReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  return (
    <Form<TransactionType>
      {...layout}
      form={form}
      name="payment-create-form"
      onFinish={handleCreate}
      style={{ maxWidth: 800 }}
      autoComplete="off"
    >
      {role === AuthRoles.Admin && (
        <>
          <Form.Item
            name="customerId"
            label={messages["common.customer"] as string}
            rules={[createYupSync({ schema: transactionSchema })]}
          >
            <Select
              loading={!customers}
              options={customers?.data?.data?.map((customer) => ({
                label: <CustomerName customer={customer} />,
                text: customer.name,
                value: customer.id,
              }))}
              showSearch
              optionFilterProp="text"
            />
          </Form.Item>
          <Form.Item
            name="providerId"
            label={messages["common.provider"] as string}
            rules={[createYupSync({ schema: transactionSchema })]}
          >
            <Select
              loading={!providers}
              options={providers?.data?.data?.map((provider) => ({
                label: <ProviderName provider={provider} />,
                text: provider.name,
                value: provider.id,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </>
      )}

      <Form.Item
        name="providerId"
        label={messages["common.provider"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Select
          options={providerCustomerMatrices?.data?.data?.map((matrix) => ({
            label: <ProviderName provider={matrix.provider} />,
            text: matrix.provider?.name,
            value: matrix.providerId,
          }))}
          showSearch
          optionFilterProp="text"
        />
      </Form.Item>

      <Form.Item
        name="amount"
        label={messages["common.amount"] as string}
        rules={[createYupSync({ schema: transactionSchema })]}
      >
        <Input
          type="number"
          autoFocus
          addonAfter={
            <Select
              placeholder={messages["common.selectOne"] as string}
              allowClear
              options={Object.values(CurrencyEnum).map((currency) => ({
                label: currency,
                value: currency,
              }))}
              showSearch
              onChange={(value) => {
                setCurrency(value);
              }}
              value={currency}
            />
          }
        />
      </Form.Item>

      <StyledFlex style={{ justifyContent: "end" }}>
        <Space>
          <Button htmlType="button" onClick={handleReset} disabled={isCreating}>
            {messages["common.reset"] as string}
          </Button>
          <Button type="primary" htmlType="submit" loading={isCreating}>
            {messages["common.create"] as string}
          </Button>
        </Space>
      </StyledFlex>
    </Form>
  );
}
